import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PROVINCES, ALICANTE_MUNICIPALITIES } from '../utils/teamDatabaseGeo';

// ─── Constants ────────────────────────────────────────────────────────────────

const BROCHURE_URL = '/experiencia-premier-league-brochure.pdf';

const LEVELS = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];
const GENDERS = ['Male', 'Female', 'Mixed'];

const KIT_COLOURS = [
  { name: 'White',    hex: '#FFFFFF', border: true },
  { name: 'Black',    hex: '#111827' },
  { name: 'Grey',     hex: '#6B7280' },
  { name: 'Red',      hex: '#DC2626' },
  { name: 'Blue',     hex: '#1D4ED8' },
  { name: 'Yellow',   hex: '#FBBF24' },
  { name: 'Green',    hex: '#16A34A' },
  { name: 'Orange',   hex: '#EA580C' },
  { name: 'Navy',     hex: '#1e3a5f' },
  { name: 'Maroon',   hex: '#7f1d1d' },
  { name: 'Sky Blue', hex: '#38bdf8' },
  { name: 'Purple',   hex: '#7c3aed' },
  { name: 'Pink',     hex: '#ec4899' },
];
const COLOUR_HEX_MAP = Object.fromEntries(KIT_COLOURS.map((c) => [c.name.toLowerCase(), c.hex]));
const JERSEY_PATTERNS = [
  { id: 'plain',   label: { en: 'Plain',        es: 'Liso' } },
  { id: 'stripes', label: { en: 'Stripes',       es: 'Rayas' } },
  { id: 'hoops',   label: { en: 'Hoops',         es: 'Aros' } },
  { id: 'halves',  label: { en: 'Half & Half',   es: 'Mitad y mitad' } },
];
const JERSEY_PATH = 'M 8,8 L 22,18 Q 36,8 50,12 Q 64,8 78,18 L 92,8 L 100,30 L 82,42 L 78,110 L 22,110 L 18,42 L 0,30 Z';

function colourToHex(name) {
  if (!name) return null;
  if (name.startsWith('#')) return name;
  return COLOUR_HEX_MAP[name.toLowerCase()] || null;
}

function patternPreviewStyle(pattern, c1hex, c2hex) {
  const a = c1hex || '#E5E7EB';
  const b = c2hex || '#FFFFFF';
  switch (pattern) {
    case 'stripes': return { background: `repeating-linear-gradient(90deg, ${a} 0px, ${a} 5px, ${b} 5px, ${b} 10px)` };
    case 'hoops':   return { background: `repeating-linear-gradient(0deg, ${a} 0px, ${a} 5px, ${b} 5px, ${b} 10px)` };
    case 'halves':  return { background: `linear-gradient(90deg, ${a} 50%, ${b} 50%)` };
    default:        return { backgroundColor: a };
  }
}

function blankSquad(i, prev) {
  return {
    label: `Squad ${i + 1}`,
    yearsOfBirth: [],
    gender: '',
    level: '',
    homeJerseyColour: prev?.homeJerseyColour || '',
    homeJerseyColour2: prev?.homeJerseyColour2 || '',
    homeJerseyPattern: prev?.homeJerseyPattern || 'plain',
    homeShortsColour: prev?.homeShortsColour || '',
    homeSocksColour: prev?.homeSocksColour || '',
    awayJerseyColour: prev?.awayJerseyColour || '',
    awayJerseyColour2: prev?.awayJerseyColour2 || '',
    awayJerseyPattern: prev?.awayJerseyPattern || 'plain',
    awayShortsColour: prev?.awayShortsColour || '',
    awaySocksColour: prev?.awaySocksColour || '',
    onlyOneKit: false,
  };
}

// ─── Translations ─────────────────────────────────────────────────────────────

const T = {
  en: {
    title: 'MST Sports Tours',
    subtitle: 'Team Database Registration',
    intro: 'Register your club to receive local fixture opportunities from visiting sports teams.',
    clubDetails: 'Club Details',
    clubName: 'Club name',
    teamType: 'Are you a school or a club?',
    teamTypeSchool: 'School',
    teamTypeClub: 'Club',
    address: 'Ground address',
    addressPlaceholder: 'Street, town, postcode',
    province: 'Province',
    selectProvince: 'Select province…',
    municipality: 'Municipality',
    selectMunicipality: 'Select municipality…',
    townPlaceholder: 'Town / municipality',
    coachDetails: 'Contact Details',
    coachName: 'Coach / contact name',
    coachEmail: 'Email address',
    coachPhone: 'Phone number',
    squads: 'Teams / Squads',
    squadLabel: (i) => `Squad ${i + 1}`,
    addSquad: '+ Add another squad',
    copyDetails: 'Copy address & contact from Squad 1',
    yearsOfBirth: 'Year(s) of birth',
    yobPlaceholder: 'e.g. 2010',
    addYear: 'Add',
    gender: 'Gender',
    selectGender: 'Select…',
    level: 'Level',
    selectLevel: 'Select level…',
    homeKit: 'Home kit',
    awayKit: 'Away kit',
    onlyOneKit: 'This team only has one kit',
    jerseyPattern: 'Jersey pattern',
    jersey: 'Jersey',
    jersey2: 'Jersey colour 2',
    shorts: 'Shorts',
    socks: 'Socks',
    preview: 'Preview',
    submit: 'Register',
    submitting: 'Registering…',
    successTitle: 'Registration received!',
    successBody: 'Thank you — your team(s) have been added to the MST Sports Tours fixture database. We will be in touch when a suitable fixture opportunity arises. To amend an entry or add more teams, email team@mstsportstours.com.',
    amendNote: 'To amend an entry or add more teams, email team@mstsportstours.com.',
    required: 'required',
    marketingConsent: 'I would like to hear about tours to England to watch Premier League football.',
    brochureDownload: 'Download the England tours brochure (PDF)',
    errorMissingClub: 'Please enter your club name.',
    errorMissingTeamType: 'Please tell us whether you are a school or a club.',
    errorMissingAddress: 'Please enter your ground address.',
    errorMissingProvince: 'Please select your province.',
    errorMissingMunicipality: 'Please select your municipality.',
    errorMissingTown: 'Please enter your town / municipality.',
    errorMissingCoachName: 'Please enter a coach / contact name.',
    errorMissingCoachEmail: 'Please enter a valid email address.',
    errorSquadYob: (l) => `Please add at least one year of birth for ${l}.`,
    errorSquadGender: (l) => `Please select a gender for ${l}.`,
    errorSquadLevel: (l) => `Please select a level for ${l}.`,
    errorSquadHomeJersey: (l) => `Please select a home jersey colour for ${l}.`,
    errorSquadHomeShorts: (l) => `Please select home shorts colour for ${l}.`,
    errorSquadHomeSocks: (l) => `Please select home socks colour for ${l}.`,
    errorSquadHomeJersey2: (l) => `Please select a second jersey colour for the ${l} home kit.`,
    errorSquadAwayJersey: (l) => `Please select an away jersey colour for ${l}, or tick "Only one kit".`,
    errorSquadAwayShorts: (l) => `Please select away shorts colour for ${l}.`,
    errorSquadAwaySocks: (l) => `Please select away socks colour for ${l}.`,
    errorSquadAwayJersey2: (l) => `Please select a second jersey colour for the ${l} away kit.`,
    genderOptions: ['Male', 'Female', 'Mixed'],
    levelOptions: ['Very Low', 'Low', 'Medium', 'High', 'Very High'],
  },
  es: {
    title: 'MST Sports Tours',
    subtitle: 'Registro en la Base de Datos de Equipos',
    intro: 'Registra tu club para recibir oportunidades de partido de equipos visitantes.',
    clubDetails: 'Datos del Club',
    clubName: 'Nombre del club',
    teamType: '¿Sois un colegio o un club?',
    teamTypeSchool: 'Colegio',
    teamTypeClub: 'Club',
    address: 'Dirección del campo',
    addressPlaceholder: 'Calle, localidad, código postal',
    province: 'Provincia',
    selectProvince: 'Selecciona provincia…',
    municipality: 'Municipio',
    selectMunicipality: 'Selecciona municipio…',
    townPlaceholder: 'Localidad / municipio',
    coachDetails: 'Datos de Contacto',
    coachName: 'Nombre del entrenador / contacto',
    coachEmail: 'Correo electrónico',
    coachPhone: 'Teléfono',
    squads: 'Equipos',
    squadLabel: (i) => `Equipo ${i + 1}`,
    addSquad: '+ Añadir otro equipo',
    copyDetails: 'Copiar dirección y contacto del Equipo 1',
    yearsOfBirth: 'Año(s) de nacimiento',
    yobPlaceholder: 'p.ej. 2010',
    addYear: 'Añadir',
    gender: 'Género',
    selectGender: 'Selecciona…',
    level: 'Nivel',
    selectLevel: 'Selecciona nivel…',
    homeKit: 'Equipación local',
    awayKit: 'Equipación visitante',
    onlyOneKit: 'Este equipo solo tiene una equipación',
    jerseyPattern: 'Diseño de camiseta',
    jersey: 'Camiseta',
    jersey2: 'Color de camiseta 2',
    shorts: 'Pantalón',
    socks: 'Medias',
    preview: 'Vista previa',
    submit: 'Registrar',
    submitting: 'Registrando…',
    successTitle: '¡Registro recibido!',
    successBody: 'Gracias — tu(s) equipo(s) ha(n) sido añadido(s) a la base de datos de MST Sports Tours. Nos pondremos en contacto cuando surja una oportunidad de partido adecuada. Para modificar un registro o añadir más equipos, escribe a team@mstsportstours.com.',
    amendNote: 'Para modificar un registro o añadir más equipos, escribe a team@mstsportstours.com.',
    required: 'obligatorio',
    marketingConsent: 'Me gustaría recibir información sobre giras a Inglaterra para ver fútbol de la Premier League.',
    brochureDownload: 'Descargar el folleto de giras a Inglaterra (PDF)',
    errorMissingClub: 'Por favor, introduce el nombre de tu club.',
    errorMissingTeamType: 'Por favor, indica si sois un colegio o un club.',
    errorMissingAddress: 'Por favor, introduce la dirección del campo.',
    errorMissingProvince: 'Por favor, selecciona la provincia.',
    errorMissingMunicipality: 'Por favor, selecciona el municipio.',
    errorMissingTown: 'Por favor, introduce la localidad / municipio.',
    errorMissingCoachName: 'Por favor, introduce el nombre del entrenador/contacto.',
    errorMissingCoachEmail: 'Por favor, introduce un correo electrónico válido.',
    errorSquadYob: (l) => `Por favor, añade al menos un año de nacimiento para ${l}.`,
    errorSquadGender: (l) => `Por favor, selecciona el género para ${l}.`,
    errorSquadLevel: (l) => `Por favor, selecciona el nivel para ${l}.`,
    errorSquadHomeJersey: (l) => `Por favor, selecciona el color de camiseta local para ${l}.`,
    errorSquadHomeShorts: (l) => `Por favor, selecciona el color de pantalón local para ${l}.`,
    errorSquadHomeSocks: (l) => `Por favor, selecciona el color de medias locales para ${l}.`,
    errorSquadHomeJersey2: (l) => `Por favor, selecciona el segundo color de camiseta local para ${l}.`,
    errorSquadAwayJersey: (l) => `Por favor, selecciona el color de camiseta visitante para ${l}, o marca "Solo una equipación".`,
    errorSquadAwayShorts: (l) => `Por favor, selecciona el color de pantalón visitante para ${l}.`,
    errorSquadAwaySocks: (l) => `Por favor, selecciona el color de medias visitante para ${l}.`,
    errorSquadAwayJersey2: (l) => `Por favor, selecciona el segundo color de camiseta visitante para ${l}.`,
    genderOptions: ['Masculino', 'Femenino', 'Mixto'],
    levelOptions: ['Muy bajo', 'Bajo', 'Medio', 'Alto', 'Muy alto'],
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function Label({ children, required: req }) {
  return (
    <label className="block text-[13px] font-medium text-[#374151] mb-1">
      {children}{req && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function Input({ value, onChange, placeholder = '', type = 'text', ...rest }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      {...rest}
    />
  );
}

function SelectEl({ value, onChange, children }) {
  return (
    <select
      value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] bg-white"
    >
      {children}
    </select>
  );
}

function KitModel({ jerseyColour, jerseyColour2, jerseyPattern, shortsColour, socksColour, uid }) {
  const c1  = colourToHex(jerseyColour)  || '#E5E7EB';
  const c2  = colourToHex(jerseyColour2) || '#FFFFFF';
  const sc  = colourToHex(shortsColour)  || '#E5E7EB';
  const sk  = colourToHex(socksColour)   || '#E5E7EB';
  const pat = jerseyPattern || 'plain';
  return (
    <svg viewBox="0 0 100 220" width="88" height="193" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <clipPath id={`jclip-${uid}`}>
          <path d={JERSEY_PATH} />
        </clipPath>
        {pat === 'stripes' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="5" height="10" fill={c1} />
            <rect x="5" y="0" width="5" height="10" fill={c2} />
          </pattern>
        )}
        {pat === 'hoops' && (
          <pattern id={`jpat-${uid}`} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <rect x="0" y="0" width="10" height="5" fill={c1} />
            <rect x="0" y="5" width="10" height="5" fill={c2} />
          </pattern>
        )}
      </defs>
      {pat === 'plain' && <path d={JERSEY_PATH} fill={c1} />}
      {(pat === 'stripes' || pat === 'hoops') && (
        <g clipPath={`url(#jclip-${uid})`}>
          <rect x="0" y="0" width="100" height="115" fill={`url(#jpat-${uid})`} />
        </g>
      )}
      {pat === 'halves' && (
        <g clipPath={`url(#jclip-${uid})`}>
          <rect x="0"  y="0" width="50" height="115" fill={c1} />
          <rect x="50" y="0" width="50" height="115" fill={c2} />
        </g>
      )}
      <path d={JERSEY_PATH} fill="none" stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="24" y="116" width="52" height="48" rx="4" fill={sc} stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="26" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
      <rect x="54" y="170" width="20" height="46" rx="3" fill={sk} stroke="#9CA3AF" strokeWidth="1.5" />
    </svg>
  );
}

function ColourInput({ value, onChange, label, required: req }) {
  return (
    <div>
      <Label required={req}>{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {KIT_COLOURS.map((c) => (
          <button
            key={c.name} type="button" title={c.name}
            onClick={() => onChange(c.name)}
            className={`w-9 h-9 rounded-full transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] focus:ring-offset-1 ${
              value === c.name ? 'ring-2 ring-[#1A4A8A] ring-offset-1 scale-110' : ''
            } ${c.border ? 'border border-[#D1D5DB]' : ''}`}
            style={{ backgroundColor: c.hex }}
            aria-label={c.name}
          />
        ))}
      </div>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Blue"
        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      />
    </div>
  );
}

function PatternSelector({ value, onChange, colour1, colour2, lang }) {
  const c1 = colourToHex(colour1) || '#E5E7EB';
  const c2 = colourToHex(colour2) || '#FFFFFF';
  return (
    <div>
      <Label>{T[lang].jerseyPattern}</Label>
      <div className="flex gap-2 flex-wrap">
        {JERSEY_PATTERNS.map((p) => (
          <button
            key={p.id} type="button" onClick={() => onChange(p.id)} aria-pressed={value === p.id}
            className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-[8px] border-2 transition ${
              value === p.id ? 'border-[#1A4A8A] bg-[#EFF6FF]' : 'border-[#E5E7EB] hover:border-[#9CA3AF]'
            }`}
          >
            <div className="w-10 h-7 rounded-[3px] border border-[#D1D5DB]" style={patternPreviewStyle(p.id, c1, c2)} />
            <span className={`text-[11px] font-medium whitespace-nowrap ${value === p.id ? 'text-[#1A4A8A]' : 'text-[#6B7280]'}`}>
              {p.label[lang]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function YearsOfBirthInput({ values, onChange, lang }) {
  const [inputVal, setInputVal] = useState('');
  const t = T[lang];
  function addYear() {
    const y = inputVal.trim();
    const n = Number(y);
    if (!y || isNaN(n) || n < 1980 || n > 2020) return;
    if (values.includes(y)) { setInputVal(''); return; }
    onChange([...values, y].sort((a, b) => a - b));
    setInputVal('');
  }
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="number" min="1980" max="2020"
          value={inputVal} onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addYear())}
          placeholder={t.yobPlaceholder}
          className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
        />
        <button
          type="button" onClick={addYear}
          className="px-3 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition"
        >
          {t.addYear}
        </button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((y) => (
            <span key={y} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#EFF6FF] border border-[#93C5FD] rounded-full text-[13px] font-semibold text-[#1A4A8A]">
              {y}
              <button type="button" onClick={() => onChange(values.filter((v) => v !== y))} className="text-[#93C5FD] hover:text-[#1A4A8A] transition leading-none">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TeamDatabase() {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') === 'es' ? 'es' : 'en';
  const t = T[lang];

  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Club-level fields
  const [clubName, setClubName]           = useState('');
  const [teamType, setTeamType]           = useState('');
  const [address, setAddress]             = useState('');
  const [province, setProvince]           = useState('Alicante');
  const [municipality, setMunicipality]   = useState('');
  const [coachName, setCoachName]         = useState('');
  const [coachEmail, setCoachEmail]       = useState('');
  const [coachPhone, setCoachPhone]       = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);

  function changeProvince(p) {
    setProvince(p);
    setMunicipality(''); // town list / free text differs per province
  }

  // Squads
  const [squads, setSquads] = useState([blankSquad(0)]);

  function addSquad() {
    setSquads((prev) => [...prev, blankSquad(prev.length, prev[0])]);
  }

  function updateSquad(i, field, value) {
    setSquads((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  }

  function copyFromFirst(i) {
    setSquads((prev) => {
      const next = [...prev];
      next[i] = {
        ...next[i],
        homeJerseyColour: next[0].homeJerseyColour,
        homeJerseyColour2: next[0].homeJerseyColour2,
        homeJerseyPattern: next[0].homeJerseyPattern,
        homeShortsColour: next[0].homeShortsColour,
        homeSocksColour: next[0].homeSocksColour,
        awayJerseyColour: next[0].awayJerseyColour,
        awayJerseyColour2: next[0].awayJerseyColour2,
        awayJerseyPattern: next[0].awayJerseyPattern,
        awayShortsColour: next[0].awayShortsColour,
        awaySocksColour: next[0].awaySocksColour,
        onlyOneKit: next[0].onlyOneKit,
      };
      return next;
    });
  }

  function removeSquad(i) {
    setSquads((prev) => prev.filter((_, idx) => idx !== i).map((sq, idx) => ({ ...sq, label: `Squad ${idx + 1}` })));
  }

  async function geocode(addr, muni, prov) {
    try {
      const q = encodeURIComponent(`${addr}, ${muni}, ${prov}, Spain`);
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`);
      const data = await res.json();
      if (data.length > 0) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    } catch {}
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');

    // Validate club fields
    if (!clubName.trim())        { setFormError(t.errorMissingClub);         return; }
    if (!teamType)               { setFormError(t.errorMissingTeamType);      return; }
    if (!address.trim())         { setFormError(t.errorMissingAddress);       return; }
    if (!province)               { setFormError(t.errorMissingProvince);      return; }
    if (!municipality.trim()) {
      setFormError(province === 'Alicante' ? t.errorMissingMunicipality : t.errorMissingTown);
      return;
    }
    if (!coachName.trim())       { setFormError(t.errorMissingCoachName);     return; }
    if (!coachEmail.trim() || !coachEmail.includes('@')) {
      setFormError(t.errorMissingCoachEmail); return;
    }

    // Validate each squad
    for (let i = 0; i < squads.length; i++) {
      const sq = squads[i];
      const l = t.squadLabel(i);
      if (!sq.yearsOfBirth?.length)              { setFormError(t.errorSquadYob(l));         return; }
      if (!sq.gender)                            { setFormError(t.errorSquadGender(l));       return; }
      if (!sq.level)                             { setFormError(t.errorSquadLevel(l));        return; }
      if (!sq.homeJerseyColour)                  { setFormError(t.errorSquadHomeJersey(l));   return; }
      if (!sq.homeShortsColour)                  { setFormError(t.errorSquadHomeShorts(l));   return; }
      if (!sq.homeSocksColour)                   { setFormError(t.errorSquadHomeSocks(l));    return; }
      if (sq.homeJerseyPattern !== 'plain' && !sq.homeJerseyColour2) {
        setFormError(t.errorSquadHomeJersey2(l)); return;
      }
      if (!sq.onlyOneKit) {
        if (!sq.awayJerseyColour)                { setFormError(t.errorSquadAwayJersey(l));   return; }
        if (!sq.awayShortsColour)                { setFormError(t.errorSquadAwayShorts(l));   return; }
        if (!sq.awaySocksColour)                 { setFormError(t.errorSquadAwaySocks(l));    return; }
        if (sq.awayJerseyPattern !== 'plain' && !sq.awayJerseyColour2) {
          setFormError(t.errorSquadAwayJersey2(l)); return;
        }
      }
    }

    setSaving(true);
    try {
      const coords = await geocode(address, municipality.trim(), province);
      await addDoc(collection(db, 'teamDatabase'), {
        clubName: clubName.trim(),
        teamType,
        address: address.trim(),
        municipality: municipality.trim(),
        province,
        region: province.toLowerCase(),
        marketingConsent,
        coachName: coachName.trim(),
        coachEmail: coachEmail.trim().toLowerCase(),
        coachPhone: coachPhone.trim(),
        lat: coords?.lat || null,
        lng: coords?.lng || null,
        squads,
        registeredAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setFormError(lang === 'es' ? 'Error al guardar. Por favor, inténtalo de nuevo.' : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-[#1A7A4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-[17px] font-bold text-[#111827] mb-2">{t.successTitle}</p>
          <p className="text-[13px] text-[#6B7280]">{t.successBody}</p>
          {marketingConsent && (
            <a
              href={BROCHURE_URL} download
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {t.brochureDownload}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="bg-[#0D2240] rounded-[12px] p-6 mb-6 text-center">
          <p className="text-white font-bold text-[18px]">{t.title}</p>
          <p className="text-[#93C5FD] text-[13px] mt-1">{t.subtitle}</p>
        </div>

        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 mb-6">
          <p className="text-[13px] text-[#6B7280]">{t.intro}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Club Details */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
            <h2 className="text-[15px] font-bold text-[#111827] mb-4">{t.clubDetails}</h2>
            <div className="space-y-4">
              <div>
                <Label required>{t.clubName}</Label>
                <Input value={clubName} onChange={(e) => setClubName(e.target.value)} />
              </div>
              <div>
                <Label required>{t.teamType}</Label>
                <div className="flex gap-4">
                  {[['school', t.teamTypeSchool], ['club', t.teamTypeClub]].map(([val, label]) => (
                    <label key={val} className="flex items-center gap-2 text-[14px] text-[#374151] cursor-pointer">
                      <input
                        type="radio" name="teamType" value={val}
                        checked={teamType === val}
                        onChange={() => setTeamType(val)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label required>{t.address}</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t.addressPlaceholder} />
              </div>
              <div>
                <Label required>{t.province}</Label>
                <SelectEl value={province} onChange={(e) => changeProvince(e.target.value)}>
                  <option value="">{t.selectProvince}</option>
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </SelectEl>
              </div>
              <div>
                <Label required>{t.municipality}</Label>
                {province === 'Alicante' ? (
                  <SelectEl value={municipality} onChange={(e) => setMunicipality(e.target.value)}>
                    <option value="">{t.selectMunicipality}</option>
                    {ALICANTE_MUNICIPALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </SelectEl>
                ) : (
                  <Input value={municipality} onChange={(e) => setMunicipality(e.target.value)} placeholder={t.townPlaceholder} />
                )}
              </div>
            </div>
          </section>

          {/* Contact Details */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
            <h2 className="text-[15px] font-bold text-[#111827] mb-4">{t.coachDetails}</h2>
            <div className="space-y-4">
              <div>
                <Label required>{t.coachName}</Label>
                <Input value={coachName} onChange={(e) => setCoachName(e.target.value)} />
              </div>
              <div>
                <Label required>{t.coachEmail}</Label>
                <Input type="email" value={coachEmail} onChange={(e) => setCoachEmail(e.target.value)} />
              </div>
              <div>
                <Label>{t.coachPhone}</Label>
                <Input type="tel" value={coachPhone} onChange={(e) => setCoachPhone(e.target.value)} />
              </div>
            </div>
          </section>

          {/* Squads */}
          <section>
            <h2 className="text-[15px] font-bold text-[#111827] mb-4">{t.squads}</h2>
            <div className="space-y-6">
              {squads.map((sq, i) => (
                <SquadCard
                  key={i} sq={sq} i={i} lang={lang} t={t}
                  updateSquad={updateSquad}
                  onCopyFromFirst={i > 0 ? () => copyFromFirst(i) : null}
                  onRemove={squads.length > 1 ? () => removeSquad(i) : null}
                />
              ))}
            </div>
            <button
              type="button" onClick={addSquad}
              className="mt-4 w-full py-2.5 border-2 border-dashed border-[#93C5FD] rounded-[10px] text-[13px] font-semibold text-[#1A4A8A] hover:bg-[#EFF6FF] transition"
            >
              {t.addSquad}
            </button>
          </section>

          {/* Marketing consent + brochure */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="rounded mt-0.5"
              />
              <span className="text-[13px] text-[#374151]">{t.marketingConsent}</span>
            </label>
            {marketingConsent && (
              <a
                href={BROCHURE_URL} download
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#1A4A8A] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#0D2240] transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t.brochureDownload}
              </a>
            )}
          </section>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-[8px] p-3 text-[13px] text-red-700">{formError}</div>
          )}

          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4">
            <p className="text-[12px] text-[#9CA3AF]">{t.amendNote}</p>
          </div>

          <button
            type="submit" disabled={saving}
            className="w-full py-3 bg-[#1A4A8A] text-white font-bold text-[15px] rounded-[10px] hover:bg-[#0D2240] transition disabled:opacity-60"
          >
            {saving ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Squad card ───────────────────────────────────────────────────────────────

function SquadCard({ sq, i, lang, t, updateSquad, onCopyFromFirst, onRemove }) {
  const genderLabels = { Male: t.genderOptions[0], Female: t.genderOptions[1], Mixed: t.genderOptions[2] };
  const levelLabels  = {
    'Very Low': t.levelOptions[0], 'Low': t.levelOptions[1], 'Medium': t.levelOptions[2],
    'High': t.levelOptions[3], 'Very High': t.levelOptions[4],
  };

  function KitSection({ kitType }) {
    const jerseyF  = `${kitType}JerseyColour`;
    const jersey2F = `${kitType}JerseyColour2`;
    const patF     = `${kitType}JerseyPattern`;
    const shortsF  = `${kitType}ShortsColour`;
    const socksF   = `${kitType}SocksColour`;
    return (
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="flex-1 space-y-3 min-w-0">
          <PatternSelector
            value={sq[patF]} onChange={(v) => updateSquad(i, patF, v)}
            colour1={sq[jerseyF]} colour2={sq[jersey2F]} lang={lang}
          />
          <ColourInput label={t.jersey} required value={sq[jerseyF]} onChange={(v) => updateSquad(i, jerseyF, v)} />
          {sq[patF] !== 'plain' && (
            <ColourInput label={t.jersey2} required value={sq[jersey2F]} onChange={(v) => updateSquad(i, jersey2F, v)} />
          )}
          <div className="grid grid-cols-2 gap-3">
            <ColourInput label={t.shorts} required value={sq[shortsF]} onChange={(v) => updateSquad(i, shortsF, v)} />
            <ColourInput label={t.socks}  required value={sq[socksF]}  onChange={(v) => updateSquad(i, socksF,  v)} />
          </div>
        </div>
        <div className="flex-shrink-0 flex flex-col items-center pt-1">
          <p className="text-[11px] text-[#9CA3AF] font-medium mb-1">{t.preview}</p>
          <KitModel
            uid={`${kitType}-${i}`}
            jerseyColour={sq[jerseyF]} jerseyColour2={sq[jersey2F]}
            jerseyPattern={sq[patF]} shortsColour={sq[shortsF]} socksColour={sq[socksF]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[14px] font-bold text-[#111827]">{t.squadLabel(i)}</h3>
        <div className="flex gap-2">
          {onCopyFromFirst && (
            <button
              type="button" onClick={onCopyFromFirst}
              className="text-[12px] text-[#1A4A8A] hover:underline font-medium"
            >
              {t.copyDetails}
            </button>
          )}
          {onRemove && (
            <button type="button" onClick={onRemove} className="text-[12px] text-red-500 hover:underline font-medium">
              {lang === 'es' ? 'Eliminar' : 'Remove'}
            </button>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <Label required>{t.yearsOfBirth}</Label>
          <YearsOfBirthInput values={sq.yearsOfBirth} onChange={(v) => updateSquad(i, 'yearsOfBirth', v)} lang={lang} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label required>{t.gender}</Label>
            <SelectEl value={sq.gender} onChange={(e) => updateSquad(i, 'gender', e.target.value)}>
              <option value="">{t.selectGender}</option>
              {GENDERS.map((g, gi) => <option key={g} value={g}>{genderLabels[g]}</option>)}
            </SelectEl>
          </div>
          <div>
            <Label required>{t.level}</Label>
            <SelectEl value={sq.level} onChange={(e) => updateSquad(i, 'level', e.target.value)}>
              <option value="">{t.selectLevel}</option>
              {LEVELS.map((l, li) => <option key={l} value={l}>{levelLabels[l]}</option>)}
            </SelectEl>
          </div>
        </div>

        <div>
          <p className="text-[13px] font-semibold text-[#374151] mb-3">{t.homeKit}</p>
          <KitSection kitType="home" />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox" id={`onlyOneKit-${i}`}
            checked={sq.onlyOneKit} onChange={(e) => updateSquad(i, 'onlyOneKit', e.target.checked)}
            className="rounded"
          />
          <label htmlFor={`onlyOneKit-${i}`} className="text-[13px] text-[#374151]">{t.onlyOneKit}</label>
        </div>

        {!sq.onlyOneKit && (
          <div>
            <p className="text-[13px] font-semibold text-[#374151] mb-3">{t.awayKit}</p>
            <KitSection kitType="away" />
          </div>
        )}
      </div>
    </div>
  );
}
